import { FixedContent, FixedPage } from '@course/components/fixed-page';
import { AdminNav, AdminTabHeader, AdminTabWrapper } from '@course/components/admin-nav';
import { rpx } from 'client/lib/rpx-client';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { serialAsync } from 'client/utils/serial-async';
import { showError } from '@components/app-error';
import { LoadingIndicator } from '@components/loading-indicator';
import { LoadedProps, RouteLoadProps, defRoute } from '@components/router';
import { HealthCards } from './health-cards';
import { MemberList } from './member-list';
import { TopDiscussions } from './top-discussions';
import { WeeklyTrend } from './weekly-trend';
import { CancellationReasons } from './cancellation-reasons';
import { RetentionTab } from './retention-tab';
import { AipMembersTab } from './aip-members-tab';
import { CoursesTab } from './courses-tab';
import { RosterTab } from './roster-tab';
import { ListsTab } from './lists-tab';
import { Segment } from './types';
import { AIP_COURSE_ID } from 'shared/mirasee-consts';
import { cachedFetch } from './dashboard-cache';

const COURSES_TTL_MS = 5 * 60 * 1000;

type Tab = 'engagement' | 'retention' | 'aip' | 'courses' | 'roster' | 'lists';

const PERIODS = [
  { key: 7, label: '7 days' },
  { key: 30, label: '30 days' },
  { key: 90, label: '90 days' },
] as const;

export const route = defRoute({
  load,
  authLevel: 'admin',
  Page,
});

type DeferredMembers = Awaited<ReturnType<typeof rpx.communityDashboard.getDashboardMembers>>;
type DeferredTopDisc = Awaited<ReturnType<typeof rpx.communityDashboard.getTopDiscussions>>;
type DeferredCancellations = Awaited<
  ReturnType<typeof rpx.communityDashboard.getCancellationReasons>
>;
type SourceCountsResult = Awaited<ReturnType<typeof rpx.engagementRetention.getSourceCounts>>;

async function load(_route: RouteLoadProps) {
  // Critical above-the-fold path only: chrome (courses) + health-card source
  // numbers + weekly trend table. Below-fold sections (members list, top
  // discussions, cancellations, source-count breakdown) are fetched after
  // mount so paint isn't gated on them.
  const [stats, courses, weeklyTrend, currentWeek] = await Promise.all([
    rpx.communityDashboard.getDashboardStats({ periodDays: 30, courseId: AIP_COURSE_ID }),
    cachedFetch(
      'dashboardCourses',
      () => rpx.communityDashboard.getDashboardCourses(),
      COURSES_TTL_MS,
    ),
    rpx.communityDashboard.getWeeklyTrend({ weeks: 12 }),
    rpx.communityDashboard.getCurrentWeekStats(),
  ]);

  const emptyMembers: DeferredMembers['members'] = [];
  const emptyTopDisc: DeferredTopDisc['discussions'] = [];
  const emptyCancellations: DeferredCancellations = { reasons: [], summary: [] };

  return {
    stats,
    members: emptyMembers,
    hasMore: false,
    courses,
    topDiscussions: emptyTopDisc,
    topDiscHasMore: false,
    weeklyTrend,
    currentWeek,
    cancellations: emptyCancellations,
    sourceCounts: undefined as SourceCountsResult | undefined,
  };
}

function readInitialTab(): Tab {
  if (typeof window === 'undefined') {
    return 'engagement';
  }
  const param = new URLSearchParams(window.location.search).get('tab');
  if (param === 'champions') {
    return 'aip';
  }
  if (param === 'retention') {
    return 'retention';
  }
  if (param === 'aip') {
    return 'aip';
  }
  if (param === 'courses') {
    return 'courses';
  }
  if (param === 'roster') {
    return 'roster';
  }
  if (param === 'lists') {
    return 'lists';
  }
  return 'engagement';
}

function selectTab(setTab: (t: Tab) => void, next: Tab) {
  setTab(next);
  if (typeof window !== 'undefined') {
    const url = `${window.location.pathname}?tab=${next}`;
    window.history.replaceState({}, '', url);
  }
}

function Page({ state, setState }: LoadedProps<typeof load>) {
  const [tab, setTab] = useState<Tab>(readInitialTab);
  const [periodDays, setPeriodDays] = useState(30);
  const [segment, setSegment] = useState<Segment>('champions');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isMembersLoading, setIsMembersLoading] = useState(true);
  const [isDiscLoading, setIsDiscLoading] = useState(true);
  const [isCancellationsLoading, setIsCancellationsLoading] = useState(true);
  const [cursor, setCursor] = useState(0);
  const [topDiscCursor, setTopDiscCursor] = useState(0);

  const isLoading = isRefreshing || isMembersLoading || isDiscLoading || isCancellationsLoading;

  // Hydrate the deferred (below-the-fold) sections after the route mounts.
  // load() ships only the above-the-fold critical path; these fill in
  // shortly after first paint without gating it.
  useEffect(() => {
    let cancelled = false;
    rpx.communityDashboard
      .getDashboardMembers({ periodDays: 30, segment: 'champions', courseId: AIP_COURSE_ID })
      .then((result) => {
        if (cancelled) {
          return;
        }
        setState((s) => ({ ...s, members: result.members, hasMore: result.hasMore }));
      })
      .catch(showError)
      .finally(() => {
        if (!cancelled) {
          setIsMembersLoading(false);
        }
      });
    rpx.communityDashboard
      .getTopDiscussions({ periodDays: 30, courseId: AIP_COURSE_ID })
      .then((result) => {
        if (cancelled) {
          return;
        }
        setState((s) => ({
          ...s,
          topDiscussions: result.discussions,
          topDiscHasMore: result.hasMore,
        }));
      })
      .catch(showError)
      .finally(() => {
        if (!cancelled) {
          setIsDiscLoading(false);
        }
      });
    rpx.communityDashboard
      .getCancellationReasons({ periodDays: 90 })
      .then((result) => {
        if (cancelled) {
          return;
        }
        setState((s) => ({ ...s, cancellations: result }));
      })
      .catch(showError)
      .finally(() => {
        if (!cancelled) {
          setIsCancellationsLoading(false);
        }
      });
    rpx.engagementRetention
      .getSourceCounts()
      .then((result) => {
        if (cancelled) {
          return;
        }
        setState((s) => ({ ...s, sourceCounts: result }));
      })
      .catch(showError);
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchStats = useMemo(() => {
    return serialAsync(async (period: number) => {
      try {
        setIsRefreshing(true);
        const [stats, membersResult, topDiscResult] = await Promise.all([
          rpx.communityDashboard.getDashboardStats({
            periodDays: period,
            courseId: AIP_COURSE_ID,
          }),
          rpx.communityDashboard.getDashboardMembers({
            periodDays: period,
            segment: 'champions',
            courseId: AIP_COURSE_ID,
          }),
          rpx.communityDashboard.getTopDiscussions({
            periodDays: period,
            courseId: AIP_COURSE_ID,
          }),
        ]);
        setState((s) => ({
          ...s,
          stats,
          members: membersResult.members,
          hasMore: membersResult.hasMore,
          topDiscussions: topDiscResult.discussions,
          topDiscHasMore: topDiscResult.hasMore,
        }));
        setSegment('champions');
        setCursor(0);
        setTopDiscCursor(0);
      } catch (err) {
        showError(err);
      } finally {
        setIsRefreshing(false);
      }
    });
  }, []);

  const fetchMembers = useMemo(() => {
    return serialAsync(async (seg: Segment, period: number, offset: number) => {
      try {
        setIsMembersLoading(true);
        const result = await rpx.communityDashboard.getDashboardMembers({
          periodDays: period,
          segment: seg,
          cursor: offset,
          courseId: AIP_COURSE_ID,
        });
        if (offset > 0) {
          setState((s) => ({
            ...s,
            members: [...s.members, ...result.members],
            hasMore: result.hasMore,
          }));
        } else {
          setState((s) => ({
            ...s,
            members: result.members,
            hasMore: result.hasMore,
          }));
        }
      } catch (err) {
        showError(err);
      } finally {
        setIsMembersLoading(false);
      }
    });
  }, []);

  const fetchMoreDiscussions = useMemo(() => {
    return serialAsync(async (period: number, offset: number) => {
      try {
        setIsDiscLoading(true);
        const result = await rpx.communityDashboard.getTopDiscussions({
          periodDays: period,
          cursor: offset,
          courseId: AIP_COURSE_ID,
        });
        setState((s) => ({
          ...s,
          topDiscussions: [...s.topDiscussions, ...result.discussions],
          topDiscHasMore: result.hasMore,
        }));
      } catch (err) {
        showError(err);
      } finally {
        setIsDiscLoading(false);
      }
    });
  }, []);

  function handlePeriodChange(e: Event) {
    const target = e.target as HTMLSelectElement;
    const newPeriod = parseInt(target.value, 10);
    setPeriodDays(newPeriod);
    setCursor(0);
    fetchStats(newPeriod);
  }

  function handleSegmentChange(newSegment: Segment) {
    setSegment(newSegment);
    setCursor(0);
    fetchMembers(newSegment, periodDays, 0);
  }

  function handleShowMore() {
    const newCursor = cursor + 10;
    setCursor(newCursor);
    fetchMembers(segment, periodDays, newCursor);
  }

  function handleShowMoreDiscussions() {
    const newCursor = topDiscCursor + 5;
    setTopDiscCursor(newCursor);
    fetchMoreDiscussions(periodDays, newCursor);
  }

  return (
    <FixedPage>
      <FixedContent class="bg-white">
        {isRefreshing && <LoadingIndicator />}
        <AdminNav currentPage="dashboard" />
        <AdminTabWrapper>
          <AdminTabHeader title="Engagement Dashboard">
            {tab === 'engagement' && (
              <div class="flex items-center gap-3">
                <a
                  href="/csv/aip-intake.csv"
                  class="text-sm text-indigo-600 hover:text-indigo-800"
                  download
                >
                  Download intake answers
                </a>
                <select class="inline-ruz-input" value={periodDays} onChange={handlePeriodChange}>
                  {PERIODS.map(({ key, label }) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </AdminTabHeader>

          <div class="flex items-center gap-2 mb-8 border-b">
            <DashboardTab
              label="Engagement"
              isActive={tab === 'engagement'}
              onClick={() => selectTab(setTab, 'engagement')}
            />
            <DashboardTab
              label="Members"
              isActive={tab === 'aip'}
              onClick={() => selectTab(setTab, 'aip')}
            />
            <DashboardTab
              label="Retention"
              isActive={tab === 'retention'}
              onClick={() => selectTab(setTab, 'retention')}
            />
            <DashboardTab
              label="Courses"
              isActive={tab === 'courses'}
              onClick={() => selectTab(setTab, 'courses')}
            />
            <DashboardTab
              label="Roster"
              isActive={tab === 'roster'}
              onClick={() => selectTab(setTab, 'roster')}
            />
            <DashboardTab
              label="Lists"
              isActive={tab === 'lists'}
              onClick={() => selectTab(setTab, 'lists')}
            />
          </div>

          {tab === 'engagement' && (
            <>
              <p class="-mt-2 mb-6 text-sm text-gray-500">
                Engagement across the AI Playground community.
              </p>
              <div class="mb-8">
                <HealthCards
                  stats={state.stats}
                  currentWeek={state.currentWeek}
                  sourceCounts={state.sourceCounts}
                />
              </div>

              <WeeklyTrend snapshots={state.weeklyTrend} courseId={AIP_COURSE_ID} />

              <MemberList
                segment={segment}
                onSegmentChange={handleSegmentChange}
                members={state.members}
                hasMore={state.hasMore}
                isLoading={isLoading}
                onShowMore={handleShowMore}
                segments={state.stats.segments}
                periodDays={periodDays}
                courseId={AIP_COURSE_ID}
              />

              <TopDiscussions
                discussions={state.topDiscussions}
                hasMore={state.topDiscHasMore}
                isLoading={isLoading}
                onShowMore={handleShowMoreDiscussions}
              />

              <CancellationReasons data={state.cancellations} />
            </>
          )}

          {tab === 'retention' && <RetentionTab />}
          {tab === 'aip' && <AipMembersTab courses={state.courses} />}
          {tab === 'courses' && <CoursesTab />}
          {tab === 'roster' && <RosterTab courses={state.courses} />}
          {tab === 'lists' && <ListsTab />}
        </AdminTabWrapper>
      </FixedContent>
    </FixedPage>
  );
}

function DashboardTab({
  label,
  isActive,
  onClick,
}: {
  label: string;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      class={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
        isActive
          ? 'border-indigo-600 text-indigo-700'
          : 'border-transparent text-gray-500 hover:text-gray-700'
      }`}
    >
      {label}
    </button>
  );
}
